import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { MessageCircle, BookOpen, ChevronRight, Users, Sparkles, X } from "lucide-react";

export default function DiscussionCards() {
  const [cards, setCards] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    axios.get(`${API}/achievements/discussion-cards`)
      .then(res => setCards(res.data))
      .catch(() => {});
  }, []);

  if (!cards) return null;

  const CARD_COLORS = [
    { bg: "linear-gradient(135deg, #9B5DE5, #7B3FD3)", border: "#7B3FD3" },
    { bg: "linear-gradient(135deg, #EF476F, #D63C5C)", border: "#D63C5C" },
    { bg: "linear-gradient(135deg, #118AB2, #0D6E91)", border: "#0D6E91" },
    { bg: "linear-gradient(135deg, #06D6A0, #049A73)", border: "#049A73" },
    { bg: "linear-gradient(135deg, #F77F00, #FFD166)", border: "#D96A00" },
  ];

  return (
    <>
      {/* Compact trigger button */}
      <button
        onClick={() => setShowModal(true)}
        className="w-full bg-white rounded-2xl shadow-md p-4 hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] transition-all text-left border-l-4"
        style={{ borderColor: "#9B5DE5" }}
        data-testid="discussion-cards-trigger"
      >
        <div className="flex items-center gap-3">
          <div className="rounded-xl p-2.5" style={{ background: "#9B5DE520" }}>
            <MessageCircle size={22} color="#9B5DE5" strokeWidth={2.5} />
          </div>
          <div className="flex-1">
            <p className="font-black text-sm text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
              Dinner Table Discussion
            </p>
            <p className="text-xs text-gray-500 font-medium">
              {cards.theme} - {cards.discussion_questions.length} conversation starters
            </p>
          </div>
          <ChevronRight size={18} className="text-gray-400" />
        </div>
      </button>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto"
            onClick={e => e.stopPropagation()} data-testid="discussion-cards-modal">
            {/* Header */}
            <div className="sticky top-0 z-10 p-4" style={{ background: "linear-gradient(135deg, #9B5DE5, #EF476F)" }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users size={22} color="white" />
                  <div>
                    <p className="text-white font-black text-lg" style={{ fontFamily: "Fredoka, sans-serif" }}>
                      Discussion Cards
                    </p>
                    <p className="text-white/70 text-xs">Week {cards.week_number}: {cards.theme}</p>
                  </div>
                </div>
                <button onClick={() => setShowModal(false)} className="bg-white/20 hover:bg-white/30 rounded-xl p-2 transition-all">
                  <X size={20} color="white" />
                </button>
              </div>
            </div>

            <div className="p-4 space-y-4">
              {/* Scripture reference */}
              <div className="bg-purple-50 rounded-xl p-3 border-l-4 border-[#9B5DE5]">
                <div className="flex items-center gap-2">
                  <BookOpen size={16} color="#9B5DE5" />
                  <span className="text-sm font-bold text-purple-700">{cards.scripture}</span>
                </div>
                <p className="text-xs text-gray-600 mt-1">{cards.teaching_focus}</p>
              </div>

              {/* Question cards */}
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Conversation Starters</p>
              {cards.discussion_questions.map((q, i) => {
                const colorSet = CARD_COLORS[i % CARD_COLORS.length];
                return (
                  <div key={i}
                    className="rounded-2xl p-5 shadow-md border-b-4 relative overflow-hidden"
                    style={{ background: colorSet.bg, borderColor: colorSet.border }}
                    data-testid={`discussion-card-${i}`}
                  >
                    <div className="absolute top-2 right-3 opacity-20">
                      <Sparkles size={24} color="white" />
                    </div>
                    <div className="flex items-start gap-3 relative z-10">
                      <div className="bg-white/20 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-white font-black text-sm">{i + 1}</span>
                      </div>
                      <p className="text-white font-bold text-base leading-relaxed"
                        style={{ fontFamily: "'Quicksand', sans-serif" }}>
                        {q}
                      </p>
                    </div>
                  </div>
                );
              })}

              {/* Family activity */}
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-4 border border-yellow-200">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles size={16} color="#F77F00" />
                  <span className="font-bold text-sm text-orange-700">Family Activity</span>
                </div>
                <p className="text-sm text-gray-700">{cards.family_activity}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
